import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import Chip from "@/components/ui/chip";
import {
  Actions as ActionsIcon,
  Eye as EyeIcon,
  Edit as EditIcon,
  Document as DocumentIcon,
  Download as DownloadIcon,
} from "@/components/icons";
import type { FilterField, TableColumn, SortOption } from "@/types/component";
import type {
  RequestAndApprovalEntry,
  RequestStatus,
} from "../types/request-and-approval.types";
import { requestAndApprovalApi } from "../services/request-and-approval.api";
import { useStore } from "@/store";
import { UserRoleType } from "@/features/roles-and-permissions/types/roles-and-permissions.types";
import { EDUCATION_TYPE_API } from "@/features/education-management/types/education-management.types";
import EducationDetailDrawer from "@/features/education-management/components/EducationDetailDrawer";
import ApprovalDetailDrawer from "../components/ApprovalDetailDrawer";
import CloseEducationModal from "@/features/request-and-approval/components/CloseEducationModal";
import { educationManagementApi } from "@/features/education-management";
import type { EducationDetailApi } from "@/features/education-management";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./RequestAndApprovalPage.module.scss";

const RequestAndApprovalPage: React.FC = () => {
  const { t } = useTranslation();
  const STATUS_LABELS: Record<RequestStatus, string> = {
    manage_requests: t("requestAndApproval.statusLabels.manage_requests"),
    sent_for_approval: t("requestAndApproval.statusLabels.sent_for_approval"),
    approved: t("requestAndApproval.statusLabels.approved"),
  };
  const navigate = useNavigate();
  const userType = useStore((s) => s.userType);
  const { hasPermission } = usePermission();
  const canCompleteEducation = hasPermission(PERMISSIONS.EDUCATION_UPDATE);

  const [entries, setEntries] = useState<RequestAndApprovalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [sortBy, setSortBy] = useState<
    | "publishedAt"
    | "requestedEnrollmentsCount"
    | "createdAt"
    | "duration"
    | "requestCollectionEndDate"
  >("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [educationDetail, setEducationDetail] =
    useState<EducationDetailApi | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approvalDetailRow, setApprovalDetailRow] =
    useState<RequestAndApprovalEntry | null>(null);
  /** Yönetici "Tamamla" tıkladığında açılan tam ekran karar modali için id */
  const [closeModalEducationId, setCloseModalEducationId] = useState<
    string | null
  >(null);
  const [exportingEducationId, setExportingEducationId] = useState<
    string | null
  >(null);

  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [instructorOptions, setInstructorOptions] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    educationManagementApi
      .getCategories()
      .then((list) => {
        setCategoryOptions(
          list.map((c) => ({ label: c.name ?? c.id, value: c.id })),
        );
      })
      .catch(() => setCategoryOptions([]));
    educationManagementApi
      .getInstructors()
      .then((list) => {
        setInstructorOptions(
          list.map((i) => {
            const id = i.userID ?? i.id;
            const label =
              [i.firstName, i.lastName].filter(Boolean).join(" ") || id;
            return { label, value: id };
          }),
        );
      })
      .catch(() => setInstructorOptions([]));
  }, []);

  // Debounce search 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await requestAndApprovalApi.getAll({
        page: currentPage,
        pageSize,
        sortBy,
        sortOrder,
        filters: {
          search: debouncedSearch || undefined,
          format: filterValues.format as
            | RequestAndApprovalEntry["format"]
            | undefined,
          status: filterValues.status as RequestStatus | undefined,
          type: filterValues.type as number | undefined,
          categoryIds: filterValues.categoryIds as string[] | undefined,
          instructorIds: filterValues.instructorIds as string[] | undefined,
        },
      });
      setEntries(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching request and approval:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch, sortBy, sortOrder, filterValues]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleOpenEducationDetail = (row: RequestAndApprovalEntry) => {
    handleDropdownToggle(row.id, false);
    setEducationDetail(null);
    setDetailLoading(true);
    educationManagementApi
      .getById(row.educationId)
      .then((res) => setEducationDetail(res.data))
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error("Education not found:", row.educationId);
      })
      .finally(() => setDetailLoading(false));
  };

  const handleCloseEducation = (row: RequestAndApprovalEntry) => {
    handleDropdownToggle(row.id, false);
    setCloseModalEducationId(row.educationId);
  };

  const handleExportEnrolledParticipants = async (row: RequestAndApprovalEntry) => {
    handleDropdownToggle(row.id, false);
    setExportingEducationId(row.educationId);
    try {
      const { blob, filename } =
        await educationManagementApi.exportEnrolledParticipants(row.educationId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        t("requestAndApproval.exportParticipantsSuccess"),
      );
    } catch {
      toast.error(
        t("requestAndApproval.exportParticipantsError"),
      );
    } finally {
      setExportingEducationId(null);
    }
  };

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const handleSortChange = (option: SortOption) => {
    switch (option.value) {
      case "publishDate_new":
        setSortBy("publishedAt");
        setSortOrder("desc");
        break;
      case "publishDate_old":
        setSortBy("publishedAt");
        setSortOrder("asc");
        break;
      case "registrationCount_high":
        setSortBy("requestedEnrollmentsCount");
        setSortOrder("desc");
        break;
      case "registrationCount_low":
        setSortBy("requestedEnrollmentsCount");
        setSortOrder("asc");
        break;
      case "createdAt_new":
        setSortBy("createdAt");
        setSortOrder("desc");
        break;
      case "createdAt_old":
        setSortBy("createdAt");
        setSortOrder("asc");
        break;
      default:
        setSortBy("createdAt");
        setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const sortOptions: SortOption[] = [
    {
      label: t("requestAndApproval.sort.publishDateNew"),
      value: "publishDate_new",
    },
    {
      label: t("requestAndApproval.sort.publishDateOld"),
      value: "publishDate_old",
    },
    {
      label: t("requestAndApproval.sort.registrationCountHigh"),
      value: "registrationCount_high",
    },
    {
      label: t("requestAndApproval.sort.registrationCountLow"),
      value: "registrationCount_low",
    },
    {
      label: t("requestAndApproval.sort.newest"),
      value: "createdAt_new",
    },
    {
      label: t("requestAndApproval.sort.oldest"),
      value: "createdAt_old",
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: "format",
      label: t("requestAndApproval.filters.format"),
      type: "dropdown",
      placeholder: t("requestAndApproval.filters.formatPlaceholder"),
      options: [
        { label: t("requestAndApproval.educationTypes.faceToFace"), value: "Yüz Yüze" },
        { label: t("requestAndApproval.educationTypes.online"), value: "Canlı" },
      ],
      searchable: false,
      multiple: false,
    },
    {
      name: "status",
      label: t("requestAndApproval.filters.status"),
      type: "dropdown",
      placeholder: t("requestAndApproval.filters.statusPlaceholder"),
      options: [
        { label: STATUS_LABELS.manage_requests, value: "manage_requests" },
        { label: STATUS_LABELS.sent_for_approval, value: "sent_for_approval" },
        { label: STATUS_LABELS.approved, value: "approved" },
      ],
      searchable: false,
      multiple: false,
    },
    {
      name: "type",
      label: t("requestAndApproval.filters.educationType"),
      type: "dropdown",
      placeholder: t("requestAndApproval.filters.educationTypePlaceholder"),
      options: [
        { label: t("requestAndApproval.educationTypes.0"), value: "0" },
        { label: t("requestAndApproval.educationTypes.1"), value: "1" },
        { label: t("requestAndApproval.educationTypes.2"), value: "2" },
        { label: t("requestAndApproval.educationTypes.3"), value: "3" },
      ],
      searchable: true,
    },
    {
      name: "categoryIds",
      label: t("requestAndApproval.filters.educationCategory"),
      type: "dropdown",
      placeholder: t("requestAndApproval.filters.educationCategoryPlaceholder"),
      options: categoryOptions,
      searchable: true,
      multiple: true,
    },
    {
      name: "instructorIds",
      label: t("requestAndApproval.filters.instructor"),
      type: "dropdown",
      placeholder: t("requestAndApproval.filters.instructorPlaceholder"),
      options: instructorOptions,
      searchable: true,
      multiple: true,
    },
  ];

  const getStatusChipType = (
    status: RequestStatus,
  ): "info" | "primary" | "success" => {
    if (status === "approved") return "success";
    return "info";
  };

  const columns: TableColumn<RequestAndApprovalEntry>[] = [
    {
      header: t("requestAndApproval.columns.educationName"),
      accessor: "educationName",
      className: "education-name-column",
    },
    {
      header: t("requestAndApproval.columns.format"),
      accessor: (row) => {
        const map: Record<string, string> = {
          "Yüz Yüze": t("requestAndApproval.educationTypes.faceToFace"),
          "Canlı": t("requestAndApproval.educationTypes.online"),
          "Üniteli": t("requestAndApproval.educationTypes.unit"),
          "Ünitesiz": t("requestAndApproval.educationTypes.nonUnit"),
        };
        return map[row.format] ?? row.format;
      },
      className: "format-column",
    },
    {
      header: t("requestAndApproval.columns.requestCount"),
      accessor: "requestCount",
      className: "request-count-column",
    },
    {
      header: t("requestAndApproval.columns.requestStart"),
      accessor: "requestStartDate",
      className: "request-start-column",
    },
    {
      header: t("requestAndApproval.columns.requestEnd"),
      accessor: "requestEndDate",
      className: "request-end-column",
    },
    {
      header: t("requestAndApproval.columns.status"),
      accessor: (row) => {
        const statusLabel = STATUS_LABELS[row.status];
        const chipType = getStatusChipType(row.status);
        return (
          <Chip type={chipType} variant="soft" size="small">
            {statusLabel}
          </Chip>
        );
      },
      className: "status-column",
    },
  ];

  const renderActions = (row: RequestAndApprovalEntry) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;

    const menuItems = [
      {
        label: t("requestAndApproval.popup.educationDetail"),
        icon: <EyeIcon width={18} height={18} />,
        onClick: () => handleOpenEducationDetail(row),
      },
      ...(userType === UserRoleType.EDUCATION_SUPERVISOR ||
      userType === UserRoleType.BRANCH_MANAGER
        ? [
            {
              label: t("requestAndApproval.popup.reviewRequests"),
              icon: <EditIcon width={18} height={18} />,
              onClick: () => {
                handleDropdownToggle(rowId, false);
                navigate(
                  `/dashboard/request-and-approval/education/${row.educationId}/officer`,
                  { state: { educationName: row.educationName } },
                );
              },
            },
          ]
        : []),

      // Manager-only aksiyonlar: EDUCATION_MANAGER/CENTRAL_MANAGER global rol
      // ile, atanmış EDUCATION_SUPERVISOR ise per-education flag ile görür.
      ...(userType === UserRoleType.CENTRAL_MANAGER ||
      userType === UserRoleType.EDUCATION_MANAGER ||
      row.isCurrentUserManager === true
        ? [
            {
              label: t("requestAndApproval.popup.planEducation"),
              icon: <EditIcon width={18} height={18} />,
              onClick: () => {
                handleDropdownToggle(rowId, false);
                navigate(
                  `/dashboard/request-and-approval/education/${row.educationId}/manager`,
                  { state: { educationName: row.educationName } },
                );
              },
            },
          ]
        : []),

      ...(userType === UserRoleType.EDUCATION_MANAGER ||
      row.isCurrentUserManager === true
        ? [
            {
              label: t("requestAndApproval.popup.downloadParticipants"),
              icon: <DownloadIcon width={18} height={18} />,
              onClick: () => handleExportEnrolledParticipants(row),
            },
          ]
        : []),

      ...(row.status === "approved"
        ? [
            {
              label: t("requestAndApproval.popup.approvalDetail"),
              icon: <DocumentIcon width={18} height={18} />,
              onClick: () => {
                handleDropdownToggle(rowId, false);
                setApprovalDetailRow(row);
              },
            },
          ]
        : []),
      // Eğitimi Kapat: yalnız onaylanmış ve UNIT olmayan eğitimler için (sene sonu manuel kapatma)
      ...(canCompleteEducation &&
      row.status === "approved" &&
      row.type !== EDUCATION_TYPE_API.UNIT
        ? [
            {
              label: t("requestAndApproval.popup.closeEducation"),
              icon: <DocumentIcon width={18} height={18} />,
              onClick: () => handleCloseEducation(row),
            },
          ]
        : []),
    ];

    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => (
          <button
            key={index}
            className={styles.actionMenuItem}
            disabled={exportingEducationId === row.educationId}
            onClick={(e) => {
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          type="button"
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("requestAndApproval.title")} />

      <PageHeader
        searchPlaceholder={t("requestAndApproval.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
      />

      <EducationDetailDrawer
        isOpen={!!educationDetail || detailLoading}
        onClose={() => setEducationDetail(null)}
        educationDetail={educationDetail}
        isLoading={detailLoading}
      />

      <ApprovalDetailDrawer
        isOpen={!!approvalDetailRow}
        onClose={() => setApprovalDetailRow(null)}
        educationId={approvalDetailRow?.educationId ?? null}
        educationName={approvalDetailRow?.educationName}
      />

      <CloseEducationModal
        isOpen={!!closeModalEducationId}
        educationId={closeModalEducationId ?? undefined}
        onClose={() => setCloseModalEducationId(null)}
        onClosed={() => fetchEntries()}
      />

      <Table
        columns={columns}
        data={entries}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("requestAndApproval.noData")}
      />
    </DashboardLayout>
  );
};

export default RequestAndApprovalPage;
